"use client";

import React from "react";
import { ArrowRight, CircleCheck as CheckCircle2, Compass, Layers, ShieldCheck, Wrench } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const renovationFeatures = [{
  id: "structural",
  title: "Structural reconfiguration",
  desc: "Load-bearing wall removal, engineered steel beams, and open-concept architectural framing that respects your home's foundational integrity.",
  badge: "Framing & Layout"
}, {
  id: "mep",
  title: "MEP modernization",
  desc: "Complete electrical service upgrades, high-efficiency HVAC zone integration, and modern plumbing manifolds for silent, reliable performance.",
  badge: "Systems & Safety"
}, {
  id: "preservation",
  title: "Historic preservation detailing",
  desc: "Careful replication of period millwork, authentic Decatur bungalow trim profiles, and architectural masonry integration.",
  badge: "Heritage Craft"
}];
const projectMetrics = [{
  label: "Neighborhood",
  value: "Oakhurst & Winnona Park"
}, {
  label: "Scope",
  value: "Full interior gut & layout redesign"
}, {
  label: "Standard",
  value: "City of Decatur Historic Code"
}];
export default function Remodeling() {
  const shouldReduceMotion = useReducedMotion();
  const easeCurve: [number, number, number, number] = [0.16, 1, 0.3, 1];
  const motionProps = shouldReduceMotion ? {
    initial: {
      opacity: 1
    },
    animate: {
      opacity: 1
    }
  } : {
    initial: {
      opacity: 0,
      y: 16
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    viewport: {
      once: true,
      amount: 0.2
    },
    transition: {
      duration: 0.4,
      ease: easeCurve
    }
  };
  return <section id="whole-home-remodeling" data-nav="light" className="relative overflow-hidden bg-background py-16 sm:py-20 lg:py-24 isolate">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-16">
          {/* Left Column (7 Columns on Large Screens) */}
          <motion.div {...motionProps} className="lg:col-span-7">
            {/* Eyebrow */}
            <div className="flex items-center gap-2">
              <span className="h-0.5 w-6 bg-primary" />
              <p className="font-sans text-xs font-bold tracking-widest text-primary uppercase">
                Decatur Craftsmanship &amp; Precision
              </p>
            </div>

            {/* Main Heading */}
            <h2 className="mt-4 font-serif text-3xl font-semibold tracking-tight text-foreground sm:text-4xl lg:text-5xl">
              Reimagining Decatur homes from the frame up
            </h2>

            {/* Intro Lead */}
            <p className="mt-5 max-w-2xl font-sans text-lg leading-relaxed text-muted-foreground">
              We take historic bungalows, mid-century ranches, and traditional residences down to the structural core, transforming compartmentalized layouts into cohesive, light-filled living spaces built for modern everyday life.
            </p>

            {/* Core Pillars List */}
            <div className="mt-8 space-y-4">
              {renovationFeatures.map((item, index) => <div key={item.id} data-index={index} className="group relative rounded-lg border border-border bg-card/60 p-5 backdrop-blur-xs transition-[border-color,box-shadow,background-color] duration-300 hover:border-primary/40 hover:bg-card hover:shadow-sm">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-baseline sm:justify-between">
                    <div className="flex items-center gap-3">
                      <div className="flex size-7 shrink-0 items-center justify-center rounded-md bg-secondary text-primary">
                        {index === 0 && <Layers className="size-4" />}
                        {index === 1 && <Wrench className="size-4" />}
                        {index === 2 && <ShieldCheck className="size-4" />}
                      </div>
                      <h3 className="font-sans text-base font-semibold text-foreground">
                        {item.title}
                      </h3>
                    </div>
                    <Badge variant="secondary" className="self-start text-[11px] font-medium tracking-wide sm:self-auto">
                      {item.badge}
                    </Badge>
                  </div>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground pl-10">
                    {item.desc}
                  </p>
                </div>)}
            </div>

            {/* Guarantees & CTA */}
            <div className="mt-8 flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="size-5 shrink-0 text-primary" />
                <span className="font-sans text-sm font-medium text-foreground">
                  Local permitting handled on your behalf
                </span>
              </div>

              <Button asChild className="bg-primary text-primary-foreground shadow-sm transition-[background-color,transform] duration-200 hover:bg-primary/90">
                <Link to="#" className="inline-flex items-center gap-2">
                  <span>Explore our process</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
            </div>
          </motion.div>

          {/* Right Column (5 Columns on Large Screens) */}
          <motion.div {...motionProps} transition={{
          duration: 0.4,
          delay: shouldReduceMotion ? 0 : 0.1,
          ease: easeCurve
        }} className="relative lg:col-span-5">
            {/* Framed Image Container */}
            <div className="relative overflow-hidden rounded-xl border border-border bg-card shadow-md">
              <div className="relative aspect-4/5 w-full overflow-hidden">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821937/img/whole_home_renovation_decatur.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821937/img/whole_home_renovation_decatur.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821937/img/whole_home_renovation_decatur.webp-wvc-sizes" alt="Finished open concept whole-home interior renovation in Decatur" loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-500 hover:scale-102" />
                <div className="absolute inset-0 bg-gradient-to-t from-foreground/40 via-transparent to-transparent pointer-events-none" />
              </div>

              {/* Floating Translucent Project Card */}
              <div className="absolute inset-x-4 bottom-4">
                <Card className="border border-border/80 bg-background/90 p-4 backdrop-blur-md shadow-lg">
                  <CardContent className="p-0">
                    <div className="flex items-center justify-between gap-2 pb-2">
                      <div className="flex items-center gap-2">
                        <Compass className="size-4 text-primary" />
                        <span className="font-sans text-xs font-bold tracking-wider text-foreground uppercase">
                          Decatur Heritage Standard
                        </span>
                      </div>
                      <Badge variant="outline" className="border-primary/30 text-primary text-[10px]">
                        Featured Scope
                      </Badge>
                    </div>

                    <Separator className="my-2 bg-border/60" />

                    <div className="grid grid-cols-1 gap-2 pt-1">
                      {projectMetrics.map((metric, i) => <div key={metric.label} data-index={i} className="flex items-center justify-between text-xs">
                          <span className="text-muted-foreground">
                            {metric.label}
                          </span>
                          <span className="font-semibold text-foreground">
                            {metric.value}
                          </span>
                        </div>)}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Background Decorative Accent */}
            <div className="pointer-events-none absolute -bottom-6 -right-6 -z-10 size-48 rounded-full bg-accent/30 blur-2xl" />
          </motion.div>
        </div>
      </div>
    </section>;
}